// Prime from anywhere on the phone: a bar that opens a small composer.
// Prime is the lead (Prime on Claude Code, lib/lead.ts): what you type is the
// owner's message in its thread (words are its `say`, so they queue offline;
// pictures go straight to the factory), and the thread opens with the reply on
// its way. With no Prime running, the words go to its start screen
// (/agents?ask=, components/PrimeAsleep.tsx), kept in a box, never lost. Talk
// starts a spoken conversation (the quick chat's brain) when the server has a
// voice agent.
import { trackUse } from '@/state/usage'
import { useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { Mic } from 'lucide-react'
import { api, mutate } from '@/runtime'
import { useAllRuns } from '@/db/hooks'
import { useUi } from '@/state/ui'
import { useAccess } from '@/state/access'
import { useAgentState } from '@/voice/agent'
import { leadLast, liveLead, sayPending } from '@/lib/lead'
import { pictureSendError } from '@/lib/runSend'
import { Sheet, cx } from '@/components/ui'
import { PrimeComposer, type ComposerMessage } from '@/components/PrimeComposer'
import { useLauncher } from './phone'
import { PrimeMark } from '@/components/PrimeMark'

/** Prime's live session (undefined while none runs, or before the runs are read). */
export const useLead = () => liveLead(useAllRuns())

/**
 * Send the owner's words (and pictures) to Prime and open its thread; with no
 * Prime running, open its start screen with the words in the box. Throws when
 * nothing was sent, which keeps a composer's draft.
 */
export const useSendToPrime = () => {
  const navigate = useNavigate()
  const lead = useLead()
  return async (text: string, images: ComposerMessage['images'] = []) => {
    trackUse('action:prime-message')
    if (!lead) {
      // a picture cannot ride the address: it stays in the composer it came from
      if (images.length) throw new Error("Prime isn't running. Start it, then send the picture.")
      void navigate({ to: '/agents', search: { ask: text } as never })
      return
    }
    if (images.length) {
      if (sayPending(lead))
        throw new Error('Waiting for your last message to reach Prime; send the picture once it has.')
      try {
        await api.factorySend(lead.spec.name ?? '', text, images)
      } catch (e) {
        throw new Error(pictureSendError(e, navigator.onLine), { cause: e })
      }
    } else await mutate.action(lead.id, 'say', { text })
    void navigate({ to: '/agents/run/$id', params: { id: lead.id } })
  }
}

export const AskPrimeSheet = ({ open, onClose }: { open: boolean; onClose: () => void }) => {
  const [busy, setBusy] = useState(false)
  const send = useSendToPrime()
  const agent = useAgentState()
  const lead = useLead()
  const last = leadLast(lead)
  const ask = async (m: ComposerMessage) => {
    setBusy(true)
    try {
      await send(m.text, m.images)
      useLauncher.getState().setOpen(false)
      onClose()
    } catch (e) {
      useUi.getState().showFlash((e as Error).message || 'Prime did not get it', 4000)
      throw e
    } finally {
      setBusy(false)
    }
  }
  return (
    <Sheet open={open} onClose={onClose} title="Ask Prime">
      <div className="flex flex-col gap-3" data-testid="ask-prime-sheet">
        {last ? (
          <p className="text-sm text-muted line-clamp-3 px-1">
            <span className="text-faint">Last: </span>
            {last}
          </p>
        ) : (
          !lead && (
            <p className="text-sm text-faint px-1" data-testid="ask-prime-asleep">
              Prime isn't running. What you write waits on its start screen.
            </p>
          )
        )}
        <PrimeComposer
          autoFocus
          busy={busy}
          onSend={ask}
          onError={(m) => useUi.getState().showFlash(m, 4000)}
        />
        {agent.available && (
          <button
            type="button"
            onClick={() => {
              onClose()
              void agent.start().catch(() => {})
            }}
            className="self-start inline-flex items-center gap-2 rounded-full bg-raised border border-line px-3.5 min-h-10 text-sm"
          >
            <Mic size={16} aria-hidden /> Talk instead
          </button>
        )}
      </div>
    </Sheet>
  )
}

/** The pill: on Home above the page dots, at the top of the launcher. */
export const AskPrimeBar = ({ className, onOpen }: { className?: string; onOpen: () => void }) => {
  const agent = useAgentState()
  // Prime is the owner's: a workspace member's token cannot reach it
  const member = useAccess((s) => s.member)
  if (member) return null
  return (
    <div className={cx('flex items-center gap-2', className)}>
      <button
        type="button"
        onClick={onOpen}
        className="flex-1 min-w-0 flex items-center gap-2.5 rounded-full bg-panel/90 border border-line/80 px-4 min-h-11 text-sm text-muted hover:text-fg shadow-[var(--shadow-pop)]"
        data-testid="ask-prime"
      >
        <PrimeMark size={16} className="text-accent shrink-0" aria-hidden />
        <span className="truncate">Ask Prime</span>
      </button>
      {agent.available && agent.status === 'disconnected' && (
        <button
          type="button"
          onClick={() => void agent.start().catch(() => {})}
          aria-label="Talk"
          className="h-11 w-11 shrink-0 rounded-full bg-panel/90 border border-line/80 text-fg-2 flex items-center justify-center"
        >
          <Mic size={17} aria-hidden />
        </button>
      )}
    </div>
  )
}
